<template>
  <div
    class="checkbox-row cherow text-noselect"
    :class="{ 'text-noselect cherow_drag': hasDragSelect }"
    @mousedown="mousedown = true"
    @mouseup="mousedown = false"
    @mouseleave="mousedown = false"
  >
    <div
      v-if="hasTitle"
      class="cherow_title gw_hvr"
      :class="[titleClass]"
    >
      <slot
        name="title"
        :item="l_item"
      >
        <div class="position-relative f-center">
          {{ item.title }}
          <GwTooltip
            v-if="item.title_tooltip || item.title_dialog"
            class="mr-1 gw_hvr_content my-auto ml-1"
            style="--hvr-content-opacity: 0.5"
            :tooltip="item.title_tooltip"
            @click="
              () => {
                if (item.title_dialog) {
                  $store.dispatch('router_set_dialogs', item.title_dialog)
                }
              }
            "
          >
            <img
              v-if="item.img && !mobile"
              class="img-tooltip"
              style="width: 20rem; border-radius: 4px"
              :src="getImage(item.img)"
            />
          </GwTooltip>
        </div>
      </slot>
      <slot name="title_after"></slot>
    </div>
    <div
      v-if="$slots.before"
      class="cherow_before"
    >
      <slot name="before" />
    </div>
    <div
      v-if="hasContent"
      class="cherow_content"
      :class="contentClass"
    >
      <div
        v-if="l_item.options.length || loading"
        class="cherow_row"
        :class="rowClass || ''"
      >
        <div
          class="cherow_row_items"
          :style="rowStyle"
          :class="[rowClass || '', { gw_loading: loading }]"
        >
          <div
            v-if="hasAny"
            class="gw_btn gw_btn_text gw_loading_text cherow_row_checkbox cherow_row_checkbox_item"
            :class="[
              { gw_btn_active: !(allDisabled || l_item.allDisabled) && !l_item.selected.length },
              { 'gw_btn_disabled no-events': l_item.anyDisabled },
            ]"
            style="
              --clr-back: var(--clr-btn-back);
              --brighten-active: 0.125;
              --clr: var(--clr-grey-2);
              --clr-hover: var(--clr-white-0);
              --clr-active: var(--clr-white-0);
              --clr-disabled: var(--clr-black-4);
              flex: var(--btn-flex);
            "
            @click="setAny"
          >
            {{ l_item.anyAsOff ? $t(`btn.off`) : $t(`practice.filter.any`) }}
          </div>
          <div
            v-for="(id, idx) in loading && !l_item.options.length ? [0, 1, 2, 4] : l_item.options"
            :key="`${id}_${idx}`"
            :data-tst="`${tstPrefix}_${id}`"
            class="gw_btn gw_btn_text gw_loading_text cherow_row_checkbox cherow_row_checkbox_item"
            :class="[
              { gw_btn_active: l_item.selected.some(i => i === id) },
              { 'gw_btn_disabled no-events': l_item.disabled.includes(id) },
            ]"
            style="
              --clr-back: var(--clr-btn-back);
              --brighten-active: 0.125;
              --clr: var(--clr-grey-2);
              --clr-hover: var(--clr-white-0);
              --clr-active: var(--clr-white-0);
              --clr-disabled: var(--clr-black-4);
              flex: var(--btn-flex);
            "
            @mousedown="handleMouseDown(id)"
            @mouseenter="setDragItem(id)"
            @click="
              setItem(
                l_item.selected.some(i => i === id),
                id,
              )
            "
          >
            <slot :name="`name_${id}`"></slot>
            <slot
              :id="id"
              name="default"
              :index="idx"
            >
              {{ id }}
            </slot>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="$slots.after"
      class="cherow_after"
    >
      <slot
        name="after"
        :item="l_item"
      />
    </div>
  </div>
</template>

<script>
import GwTooltip from '@/components/lib/GwTooltip'
import float_mix from '@/mixins/float_mix'

function getInitialItem() {
  return {
    id: 'unset',
    title: '',
    title_tooltip: '',
    selected: [],
    options: [],
    disabled: [],
    anyDisabled: false,
    allDisabled: false,
    onlyAfter: false,
    anyAsOff: false,
  }
}

export default {
  name: 'CheckboxRow',
  components: {
    GwTooltip,
  },
  mixins: [float_mix],
  props: {
    // hasNamedTitleSlot: Boolean,
    checkboxAs: { default: () => 'btn' },
    hasAny: { type: Boolean, default: true },
    hasInitialUpdate: { type: Boolean, default: true },
    isSingle: Boolean,
    item: {
      default: () => getInitialItem(),
    },
    styleCheckbox: [Object, String],
    styleRowCheckbox: [Object, String],
    hasDragSelect: Boolean,
    asDragSelect: Boolean,
    hasContent: { type: Boolean, default: true },
    open: { type: Boolean, default: true },
    loading: Boolean,
    allDisabled: Boolean,
    hasTitle: { type: Boolean, default: true },
    checkboxIcon: Array,
    titleClass: String,
    checkboxIcons: Array,
    stylesCheckbox: Array,
    rowClass: String,
    rowStyle: String,
    checkboxClass: [String, Object],
    contentClass: String,
    tstPrefix: { default: 'chrow' },
  },
  emits: ['update:any', 'update:item', 'update'],
  data() {
    return {
      l_item: getInitialItem(),
      mousedown: false,
      toggleValue: false,
    }
  },
  methods: {
    setAny() {
      const res = {
        ...this.l_item,
        selected: [],
      }
      this.update(res)
      this.$emit('update:any', res)
    },
    handleMouseDown(id) {
      if (this.hasDragSelect) {
        this.mousedown = true
        this.toggleValue = this.l_item.selected.some(i => i === id)
        this.setDragItem(id)
      }
    },
    setDragItem(id) {
      if (this.hasDragSelect && this.mousedown) {
        // console.log(id, selected)
        this.setItem(this.toggleValue, id, 'force')
      }
    },
    setItem(selected, id, force) {
      if (!force && this.hasDragSelect) return
      let res = this.l_item
      if (this.isSingle) {
        res = {
          ...res,
          selected: [id],
        }
      } else {
        res = {
          ...res,
          selected: selected
            ? this.l_item.selected.filter(i => i !== id)
            : [...this.l_item.selected, id],
        }
      }
      this.update(res)
      this.$emit('update:item', id)
    },
    update(res) {
      if (res.id === 'unset') return
      this.$emit('update', res)
    },
    setLocalItem() {
      if (this.loading) return

      const res = {
        ...getInitialItem(),
        ...this.item,
      }

      const enabledOptions = res.options.filter(id => !res.disabled.includes(id))
      let selected = res?.selected.filter(id => {
        const r = enabledOptions.includes(id)
        return r
      })
      if (res.selected.length && !selected.length && res.hasAny) {
        this.l_item = {
          ...res,
          selected: [],
        }
        this.setAny()
        return
      }

      if (this.isSingle && !this.hasAny) {
        selected = [selected[0] || enabledOptions[0]]
      }

      this.l_item = {
        ...res,
        selected,
      }

      if (this.hasInitialUpdate) {
        const item = {
          ...getInitialItem(),
          ...this.item,
        }
        if (JSON.stringify(item) !== JSON.stringify(this.l_item)) {
          this.update(this.l_item)
        }
      }
    },
  },

  watch: {
    item: {
      handler: 'setLocalItem',
      immediate: true,
      // deep: true,
    },
    isSingle: {
      handler: 'setLocalItem',
      immediate: true,
      // deep: true,
    },
    loading: {
      handler: 'setLocalItem',
      immediate: true,
    },
  },
}
</script>
<style lang="scss" scoped>
.cherow {
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
  transition: 0.3s;
  border-radius: 4px;
  flex-shrink: 0;

  --clr-btn-back: var(--clr-black-1-2);

  --size: 1rem;

  --max-height: 68em;
  --grid-gap: 0.25em;
  --padding-title: 0 0 0.25em 0;
  --padding-content: 0;
  --padding-row: 0;
  --width-row: auto;
  --max-height-row: auto;

  --margin-content: 0;
  --clr-row-back: transparent;
  --cherow-shadow: none;

  --grid-gap: 2px;
  --display-items-row: flex;
  --columns-items-row: unset;
  --row-wrap: wrap;

  --padding-content: 0;
  --padding-row: 2px;
  --overflow-x-row: unset;
  --overflow-y-row: unset;
  --clr-row-back: transparent;
  --margin-content: 0 0 0 0;

  --clr-title: var(--clr-grey-2);
  --title-size: 0.875em;
  --title-margin: 0 0 0.25rem 0;
  --checkbox-item-min-width: auto;
  --checkbox-item-max-width: auto;
  --checkbox-item-height: 1.875rem;

  &_single {
    --grid-gap: 0.125em;
    --padding-content: 0;
    --padding-row: 0.25em 0.25em;
    --clr-row-back: transparent;
    --margin-content: 0 0 0 0;
  }

  &_title {
    align-items: center;
    color: var(--clr-title);
    font-size: var(--title-size);
    margin: var(--title-margin);
    width: 100%;
    position: relative;
    display: flex;
    &_tooltip {
      position: relative;
    }
    &_disabled {
      color: var(--clr-grey-2);
      pointer-events: none;
      .cherow_title_icon {
        opacity: 0;
      }
    }
    &_icon {
      transition: 0.3s;
      margin-left: auto;
    }
  }
  &_drag {
    .cherow_checkbox-any {
      margin-right: 0.125em;
    }
  }
  .cherow_checkbox-any {
    position: sticky;
    top: 0;
  }
  &_grid {
    width: 100%;
    --width-row: 100%;
    --display-items-row: grid;
    --max-height-row: auto;
    --overflow-x-row: hidden;
    --overflow-y-row: auto;
    --columns-items-row: repeat(auto-fit, minmax(3em, 1fr));
    --gap: 0px;
  }
  &_content {
    padding: 0;
    margin: var(--margin-content);
    padding: var(--padding-content);
    max-height: var(--max-height-row);
    box-shadow: var(--cherow-shadow);
    align-self: flex-start;
    width: 100%;
    display: flex;
    max-height: var(--max-height);
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
    overflow-x: var(--overflow-x-row);
    overflow-y: var(--overflow-y-row);
  }
  &_vertical {
    .cherow_row {
      flex-direction: column;
      min-width: 6em;

      --overflow-x-row: hidden;
      --overflow-y-row: auto;
      &_items {
        flex-direction: column;
      }
      &_checkbox {
        width: 100%;
      }
    }
  }
  &_row {
    width: var(--width-row);
    max-height: var(--max-height-row);

    grid-gap: var(--grid-gap);
    padding: var(--padding-row);

    display: flex;
    border-radius: 4px;
    align-self: flex-start;
    background: var(--clr-row-back);
    &_checkbox {
      align-self: flex-start;
    }
    &_items {
      --gap: var(--grid-gap);
      display: var(--display-items-row);
      grid-template-columns: var(--columns-items-row);
      grid-gap: var(--gap);
      border-radius: 4px;
      flex-wrap: var(--row-wrap);
      flex: 1;
    }
  }
  .cherow_row_checkbox_item {
    min-width: var(--checkbox-item-min-width);
    max-width: var(--checkbox-item-max-width);
    height: var(--checkbox-item-height);
    min-width: 2rem;
    font-size: var(--size);
    font-weight: 500;
  }
}
</style>
